import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import {
  authedFetch,
  hasStoredSession,
  useStoredAuth,
  type StoredAuth,
} from "nazare-ui/utils";
import { subscribeAuthenticatedSse } from "./authenticated-sse";
import { resolveBffDomain } from "./bff-domain";
import { workflowRunsQueryKey, workflowRunsRequestParams } from "./workflow-runs-query";
import { withAbortTimeout } from "./workflow-history-timeout";
import {
  workflowStepStatusCsv,
  normaliseWorkflowStatus,
  mergeWorkflowStepHeadSnapshot,
  mergeWorkflowStepRefreshedPage,
  workflowStepHeadRefetchInterval,
  workflowStepRefetchInterval,
  workflowStepWarmingRefetchInterval,
} from "./workflow-step-filter";
import {
  workflowStepBranchPollingEnabled,
  workflowStepBranchRequestEnabled,
  workflowStepBranchNextCursor,
} from "./workflow-branch-controller";
import {
  fetchWorkflowStepPageUntilReady,
  workflowStepHistoryInitialPageParam,
  workflowStepHistoryQueryOptions,
  workflowStepsRequestPath,
} from "./workflow-step-pagination";
import { mergeWorkflowRunSnapshot, workflowRunDetailPath, type WorkflowRunSnapshotKind } from "./workflow-run-snapshot";
import { workflowProgressPath, type WorkflowProgressQuery } from "./workflow-progress";
import {
  invalidateWorkflowStepQueries,
  parseWorkflowObservabilityAdvanceResponse,
  workflowObservabilityAdvancePath,
  WorkflowObservabilityAdvanceGate,
  workflowStepsWarming,
} from "./workflow-observability-advance";

export { workflowProgressPath };
export type { WorkflowProgressQuery };

const runStreamStatus = new Map<string, boolean>();
const runStreamListeners = new Map<string, Set<() => void>>();
const streamKey = (source: string, runId: string) => `${source}:${runId}`;
function setRunStreamStatus(key: string, connected: boolean) {
	runStreamStatus.set(key, connected);
	runStreamListeners.get(key)?.forEach((listener) => listener());
}
function useRunStreamConnected(source: string, runId: string) {
	const key = streamKey(source, runId);
	return useSyncExternalStore(
		(listener) => {
			const listeners = runStreamListeners.get(key) ?? new Set();
			listeners.add(listener);
			runStreamListeners.set(key, listeners);
			return () => listeners.delete(listener);
		},
		() => runStreamStatus.get(key) ?? false,
		() => false,
	);
}

export type RunSummary = {
  runId: string;
  workflowName: string;
  workflowShortName: string;
  status: "queued" | "running" | "sleeping" | "paused" | "completed" | "failed" | "timed_out" | "cancelled" | "rerun";
  pauseSupported?: boolean;
  pauseRequestedAt?: number | null;
  pauseAcknowledgedAt?: number | null;
  controlState?: "running" | "pause_requested" | "paused";
  deploymentId: string | null;
  triggeredBy: string | null;
  /** Full run snapshots may enrich this with computed retry progress; fast overviews may omit it. */
  label?: string | null;
  retryOf?: string | null;
  attempt?: number | null;
  createdAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  error: unknown | null;
  sentryEventId?: string | null;
  artifactCount: number | null;
};

export type StepSummary = {
  stepId: string;
  /** Stable tree identity supplied by the progressive run-step endpoint. */
  nodeKey?: string;
  /** Stable parent identity; omitted for top-level roots. */
  parentKey?: string | null;
  /** True when the endpoint can return another level below this row. */
  hasChildren?: boolean;
  /** Planned root/branch row with no persisted physical step yet. */
  isPlaceholder?: boolean;
  /** Immutable logical page/sibling position when supplied by the workflow backend. */
  pageOrdinal?: number | null;
  stepName: string;
  stepShortName: string;
  status: string;
  attempt: number;
  createdAt: string | null;
  startedAt: string | null;
  updatedAt: string | null;
  input: unknown | null;
  output: unknown | null;
  error: unknown | null;
  executedByDeploymentId?: string | null;
  defaultCollapsed?: boolean;
  tree?: Array<{ key: string; label: string; collapsed?: boolean }>;
  /** Opaque JSON progress embedded by the scoped step response; never inferred by the UI. */
  progress?: WorkflowProgressJsonValue | null;
};

export type EventSummary = {
  eventId: string;
  eventType: string;
  correlationId: string | null;
  createdAt: string | null;
};

export type DiagnosticSummary = {
  eventId: string;
  severity: "warn" | "error";
  code: string;
  message: string;
  stepId: string | null;
  createdAt: string | null;
};

export type DiagnosticCounts = { warn: number; error: number };

export type ListRunsResponse = {
  source: string;
  data: RunSummary[];
  cursor: string | null;
  hasMore: boolean;
};

export type RunDetailTreeRoot = {
  key: string;
  label: string;
  status: string;
  coverage: "complete" | "partial" | "unknown";
  /** Opaque generic progress for inventory roots, including roots without child steps. */
  progress?: WorkflowProgressJsonValue | null;
  /** Persisted physical root rows, including discovery and renewal rows. */
  rootSteps?: StepSummary[];
  children: StepSummary[];
  childrenLoaded: number;
  childrenHasMore: boolean;
};

export type RunDetailTree = {
  roots: RunDetailTreeRoot[];
  rootsComplete: boolean;
  inventorySource: "discovery" | "steps";
};

export type RunDetailResponse = {
  source: string;
  run: RunSummary;
  attempts: Array<{ runId: string; attempt: number; status: string; createdAt: string | null }>;
  steps: StepSummary[];
  /** Opt-in progressive tree inventory returned when the detail request uses view=tree. */
  tree?: RunDetailTree;
  /** Complete counts by step status. Omitted by fast overview snapshots. */
  stepStatusCounts?: Record<string, number> | null;
  /** Omitted by fast overview snapshots. */
  durableWaitCounts?: { sleeping: number; ready: number } | null;
  stepsTruncated: boolean;
  events: EventSummary[];
  eventsTruncated: boolean;
  diagnostics: DiagnosticSummary[];
  diagnosticsTruncated: boolean;
  /** Omitted by fast overview snapshots. */
  diagnosticCounts?: DiagnosticCounts | null;
};

export type WorkflowProgressJsonValue =
  | null
  | boolean
  | number
  | string
  | WorkflowProgressJsonValue[]
  | { [key: string]: WorkflowProgressJsonValue };
export type WorkflowProgressScope = "run" | "step";
type WorkflowProgressEntryBase = {
  payload: Record<string, WorkflowProgressJsonValue>;
  metadata?: Record<string, WorkflowProgressJsonValue>;
  scopeKey?: string;
  generation: string;
  attempt: number;
  revision: number;
  idempotencyKey: string;
  updatedAt: string;
};
export type WorkflowProgressEntry = (
  | (WorkflowProgressEntryBase & { scope: "run"; stepId?: never })
  | (WorkflowProgressEntryBase & { scope: "step"; stepId: string })
);
export type WorkflowProgressResponse = {
  runId: string;
  coverage: {
    state: "ready" | "warming";
    complete: boolean;
    returned: number;
    asOf: string;
  };
  snapshots: WorkflowProgressEntry[];
};

export type WorkflowStatusHistoryPoint = { timestamp: string; byStatus: Record<string, number> };
export type WorkflowStatusHistoryResponse = {
  source: string;
  period: string;
  points: WorkflowStatusHistoryPoint[];
  status?: "warming" | "ready";
  asOf?: string;
};

export type RunStepsResponse = {
  source: string;
  data: StepSummary[];
  cursor: string | null;
  hasMore: boolean;
  status?: "warming" | "ready";
  /** A ready prefix can still be incomplete while the child projection warms. */
  coverage?: "warming" | "ready";
  retryAfterSeconds?: number;
};

export type RunEventsResponse = {
  source: string;
  data: EventSummary[];
  cursor: string | null;
  hasMore: boolean;
};

export type LogLine = {
  eventId: string;
  timestamp: number;
  level?: number | null;
  msg?: string | null;
  fields?: Record<string, unknown> | null;
  raw?: string | null;
};

export type RunLogsResponse = {
  lines: LogLine[];
  nextCursor?: string | null;
  logGroupConfigured?: boolean | null;
};

export type ActiveRun = RunSummary & { diagnostics: DiagnosticCounts };
export type ActiveResponse = {
  source: string;
  total: number;
  activeSteps?: number;
  byStatus: Record<string, number>;
  runs: ActiveRun[];
  diagnostics: DiagnosticCounts;
  truncated: boolean;
};

export type WorkflowSource = { id: string; label: string; kind: string };
export type WorkflowSourcesResponse = { sources: WorkflowSource[] };

export type ScheduleTarget = { id: string; arn: string; kind?: string | null };
export type ScheduleSummary = {
  name: string;
  label?: string | null;
  category?: "workflow" | "infra" | null;
  scheduleExpression: string;
  state: "ENABLED" | "DISABLED";
  description?: string | null;
  nextFireTime?: string | null;
  targets: ScheduleTarget[];
};
export type SchedulesResponse = {
  source: string;
  schedules: ScheduleSummary[];
  truncated: boolean;
};

export type WorkflowFieldWidget =
  | "text"
  | "textarea"
  | "userEmail"
  | "multiUserEmail"
  | "emailSearch"
  | "checkbox"
  | "oneOf";

export type WorkflowInputFieldBranch = {
  key: string;
  label: string;
  fields: WorkflowInputField[];
};

export type WorkflowInputField = {
  key: string;
  label: string;
  widget: WorkflowFieldWidget;
  type: "string" | "stringArray" | "boolean";
  placeholder?: string;
  required?: boolean;
  order?: number;
  valueKey?: "email" | "classificationId";
  branches?: WorkflowInputFieldBranch[];
};

export type WorkflowDefinition = {
  name: string;
  shortName: string;
  label: string;
  description: string;
  inputFields: WorkflowInputField[];
  allowManual: boolean;
  immortal: boolean;
};
export type WorkflowDefinitionsResponse = {
  definitions: WorkflowDefinition[];
  statuses?: Array<{ value: string; label: string }>;
};

export type ArtefactSummary = {
  runId: string;
  artifactId: string;
  name: string;
  stepId: string | null;
  contentType: string;
  storage: "s3";
  size: number;
  createdAt: string | null;
};
export type ListArtefactsResponse = {
  source: string;
  runId: string;
  data: ArtefactSummary[];
  cursor: string | null;
  hasMore: boolean;
};
export type GetArtefactResponse = {
  source: string;
  artifact: ArtefactSummary;
  content: { kind: "s3"; downloadUrl: string; expiresInSeconds: number };
};

export type EmailSearchResponse = {
  results: Array<{
    email: string;
    subject?: string | null;
    companyName?: string | null;
    date?: string | null;
    classificationId?: string | null;
  }>;
};

export const DEFAULT_WORKFLOW_SOURCE = "aws";
export const ACTIVE_WORKFLOW_STATUSES = new Set(["queued", "running", "sleeping", "paused"]);
export const isActiveWorkflowStatus = (status: string) => ACTIVE_WORKFLOW_STATUSES.has(status);

const WORKFLOWS_BASE = `${resolveBffDomain()}/workflows`;
const observabilityAdvanceGate = new WorkflowObservabilityAdvanceGate();

type WorkflowObservabilityAdvanceMutation = {
  request: Promise<ReturnType<typeof parseWorkflowObservabilityAdvanceResponse>>;
  queryKeys: readonly QueryKey[];
};

async function requestWorkflowObservabilityAdvance(
  auth: StoredAuth | null,
  runId: string,
  source: string,
) {
  const response = await authedFetch(
    WORKFLOWS_BASE,
    auth,
    workflowObservabilityAdvancePath(runId, source),
    { method: "POST" },
  );
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }
  return parseWorkflowObservabilityAdvanceResponse(response.status, body);
}

function useWorkflowObservabilityAdvance({
  auth,
  runId,
  source,
  enabled,
  warming,
  observedAt,
  queryKeys,
}: {
  auth: StoredAuth | null;
  runId: string;
  source: string;
  enabled: boolean;
  warming: boolean;
  observedAt: number;
  queryKeys: readonly QueryKey[];
}) {
  const client = useQueryClient();
  const advanceKey = `${source}\u0000${runId}`;
  const mutation = useMutation({
    mutationKey: ["workflows", "observability-advance", source, runId],
    scope: { id: `workflow-observability-advance:${advanceKey}` },
    mutationFn: ({ request }: WorkflowObservabilityAdvanceMutation) => request,
    onSuccess: async (result, variables) => {
      if (result.statusCode === 200 || result.statusCode === 202) {
        await invalidateWorkflowStepQueries(client, variables.queryKeys);
      }
    },
  });
  const mutateAsync = mutation.mutateAsync;

  useEffect(() => {
    if (!enabled || !observabilityAdvanceGate.isEligible(advanceKey, warming)) return;
    const request = observabilityAdvanceGate.start(
      advanceKey,
      () => requestWorkflowObservabilityAdvance(auth, runId, source),
    );
    if (request) void mutateAsync({ request, queryKeys }).catch(() => undefined);
  }, [advanceKey, auth, enabled, mutateAsync, observedAt, queryKeys, runId, source, warming]);
}

async function readJson<T>(response: Response, context: string): Promise<T> {
  if (!response.ok) throw new Error(`${context}: ${response.status}`);
  return response.json() as Promise<T>;
}

async function workflowFetch<T>(
  auth: StoredAuth | null,
  path: string,
  context: string,
  init?: RequestInit,
): Promise<T> {
  return readJson<T>(await authedFetch(WORKFLOWS_BASE, auth, path, init), context);
}

export function useWorkflowRuns({
  source = DEFAULT_WORKFLOW_SOURCE,
  workflowName = [],
  status = [],
  period = "24h",
  from,
  to,
  limit = 50,
}: {
  source?: string;
  workflowName?: string[];
  status?: string[];
  period?: "1h" | "24h" | "7d" | "30d" | "custom";
  from?: string;
  to?: string;
  limit?: number;
} = {}) {
  const auth = useStoredAuth();
  const filters = { source, workflow: workflowName, status, period, from, to, limit };
  return useInfiniteQuery({
    queryKey: workflowRunsQueryKey(filters),
    enabled: hasStoredSession(auth),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const params = workflowRunsRequestParams(filters);
      if (pageParam) params.set("cursor", pageParam);
      return workflowFetch<ListRunsResponse>(auth, `/runs?${params}`, "Failed to fetch workflow runs");
    },
    getNextPageParam: (last) => (last.hasMore ? last.cursor ?? undefined : undefined),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useWorkflowStatusHistory(period: "1h" | "24h" | "7d" | "30d", source = DEFAULT_WORKFLOW_SOURCE) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["workflows", "status-history", source, period],
    enabled: hasStoredSession(auth),
    queryFn: ({ signal }) => withAbortTimeout(
      (requestSignal) => workflowFetch<WorkflowStatusHistoryResponse>(
        auth,
        `/history?source=${encodeURIComponent(source)}&period=${period}`,
        "Failed to fetch workflow status history",
        { signal: requestSignal },
      ),
      35_000,
      signal,
    ),
    // The history read has a server-side deadline. Retrying an exhausted read
    // keeps the dashboard in a loading state for minutes without producing data.
    retry: false,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useWorkflowSources() {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["workflows", "sources"],
    enabled: hasStoredSession(auth),
    queryFn: () => workflowFetch<WorkflowSourcesResponse>(auth, "/sources", "Failed to fetch workflow sources"),
    staleTime: 60_000,
  });
}

export function useActiveWorkflows(source = DEFAULT_WORKFLOW_SOURCE) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["workflows", "active", source],
    enabled: hasStoredSession(auth),
    queryFn: () =>
      workflowFetch<ActiveResponse>(auth, `/active?source=${encodeURIComponent(source)}`, "Failed to fetch active workflows"),
    placeholderData: keepPreviousData,
    refetchInterval: 8_000,
  });
}

export function useActiveWorkflowsStream(source = DEFAULT_WORKFLOW_SOURCE) {
  const auth = useStoredAuth();
  const client = useQueryClient();
  const previousIds = useRef<string | null>(null);
  useEffect(() => {
    if (!hasStoredSession(auth)) return;
    return subscribeAuthenticatedSse({
      baseUrl: WORKFLOWS_BASE,
      auth,
      path: `/active/stream?source=${encodeURIComponent(source)}`,
      onMessage: ({ event, data }) => {
        if (event !== "active") return;
        try {
          const snapshot = JSON.parse(data) as ActiveResponse;
          client.setQueryData(["workflows", "active", source], snapshot);
          const ids = snapshot.runs.map((run) => run.runId).sort().join(",");
          if (previousIds.current !== null && previousIds.current !== ids) {
            void client.invalidateQueries({ queryKey: ["workflows", "runs", source] });
          }
          previousIds.current = ids;
        } catch { /* Ignore keep-alive or malformed frames. */ }
      },
    });
  }, [auth, client, source]);
}

export function useWorkflowDefinitions() {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["workflows", "definitions"],
    enabled: hasStoredSession(auth),
    queryFn: () =>
      workflowFetch<WorkflowDefinitionsResponse>(auth, "/definitions", "Failed to fetch workflow definitions"),
    staleTime: 60_000,
  });
}

export function useWorkflowSchedules(source = DEFAULT_WORKFLOW_SOURCE) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["workflows", "schedules", source],
    enabled: hasStoredSession(auth),
    queryFn: () =>
      workflowFetch<SchedulesResponse>(auth, `/schedules?source=${encodeURIComponent(source)}`, "Failed to fetch workflow schedules"),
    staleTime: 60_000,
  });
}

export function useWorkflowRunDetail(runId: string, source = DEFAULT_WORKFLOW_SOURCE) {
  const auth = useStoredAuth();
  const client = useQueryClient();
  const queryKey = ["workflows", "run-detail", source, runId] as const;
  const streamConnected = useRunStreamConnected(source, runId);
  return useQuery({
    queryKey,
    enabled: hasStoredSession(auth) && !!runId,
    queryFn: async ({ signal }) => {
      const requestStartedData = client.getQueryData<RunDetailResponse>(queryKey);
      const fetched = await withAbortTimeout(
        (requestSignal) => workflowFetch<RunDetailResponse>(
          auth,
          workflowRunDetailPath(runId, source),
          "Failed to fetch workflow run",
          { signal: requestSignal },
        ),
        30_000,
        signal,
        "Workflow run request timed out",
      );
      const latestData = client.getQueryData<RunDetailResponse>(queryKey);
      return mergeWorkflowRunSnapshot(
        latestData,
        fetched,
        "overview",
        { preserveCurrentDetails: latestData !== undefined && latestData !== requestStartedData },
      );
    },
    retry: false,
    refetchInterval: (query) =>
      !streamConnected && query.state.data && isActiveWorkflowStatus(query.state.data.run.status)
        ? 5_000
        : false,
  });
}

export function useWorkflowRunProgress(
  runId: string,
  source = DEFAULT_WORKFLOW_SOURCE,
  query: WorkflowProgressQuery = { scope: "run" },
  enabled = true,
  pollingEnabled = false,
) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["workflows", "run-progress", source, runId, query],
    enabled: enabled && hasStoredSession(auth) && !!runId,
    queryFn: () =>
      workflowFetch<WorkflowProgressResponse>(
        auth,
        workflowProgressPath(runId, source, query),
        "Failed to fetch workflow progress",
      ),
    retry: false,
    staleTime: 5_000,
    refetchInterval: pollingEnabled ? 5_000 : false,
  });
}

export function useWorkflowRunDetailStream(runId: string, source = DEFAULT_WORKFLOW_SOURCE) {
  const auth = useStoredAuth();
  const client = useQueryClient();
  const wasActive = useRef<boolean | null>(null);
  useEffect(() => {
    if (!hasStoredSession(auth) || !runId) return;
    const applySnapshot = (snapshot: RunDetailResponse, kind: WorkflowRunSnapshotKind) => {
      const merged = client.setQueryData<RunDetailResponse>(
        ["workflows", "run-detail", source, runId],
        (current) => mergeWorkflowRunSnapshot(current, snapshot, kind),
      );
      return merged ?? snapshot;
    };
    return subscribeAuthenticatedSse({
      baseUrl: WORKFLOWS_BASE,
      auth,
      path: `/runs/${encodeURIComponent(runId)}/stream?source=${encodeURIComponent(source)}&view=tree`,
      onConnected: () => setRunStreamStatus(streamKey(source, runId), true),
      onError: () => setRunStreamStatus(streamKey(source, runId), false),
      onMessage: ({ event, data }) => {
        if (event !== "overview" && event !== "run") return;
        try {
          const snapshot = JSON.parse(data) as RunDetailResponse;
          const merged = applySnapshot(snapshot, event);
          void client.invalidateQueries({ queryKey: ["workflows", "run-progress", source, runId] });
          const active = isActiveWorkflowStatus(merged.run.status);
          if (wasActive.current === true && !active) {
            void client.invalidateQueries({ queryKey: ["workflows", "runs", source] });
          }
          wasActive.current = active;
        } catch { /* Ignore keep-alive or malformed frames. */ }
      },
    });

  }, [auth, client, runId, source]);
  useEffect(() => () => setRunStreamStatus(streamKey(source, runId), false), [runId, source]);
}

export function useWorkflowRunSteps(
  runId: string,
  active: boolean,
  source = DEFAULT_WORKFLOW_SOURCE,
  statuses: readonly string[] = [],
  parentKey?: string,
  queryEnabled = true,
  view: "flat" | "tree" = "flat",
) {
  const auth = useStoredAuth();
  const statusCsv = workflowStepStatusCsv(statuses);
  const enabled = hasStoredSession(auth) && !!runId && queryEnabled;
  const headQueryKey = useMemo(
    () => ["workflows", "run-steps-head", source, runId, statusCsv, parentKey ?? null, view] as const,
    [parentKey, runId, source, statusCsv, view],
  );
  const historyQueryKey = useMemo(
    () => ["workflows", "run-steps-history", source, runId, statusCsv, parentKey ?? null, view] as const,
    [parentKey, runId, source, statusCsv, view],
  );
  const advanceKey = `${source}\u0000${runId}`;
  const identity = `${source}\u0000${runId}\u0000${statusCsv}\u0000${parentKey ?? ""}\u0000${view}`;
  const headArchive = useRef<{ identity: string; page: RunStepsResponse | null }>({ identity, page: null });
  const historyState = useRef<{ identity: string; startCursor: string | null | undefined }>({ identity, startCursor: undefined });
  if (headArchive.current.identity !== identity) {
    headArchive.current = { identity, page: null };
    historyState.current = { identity, startCursor: undefined };
  }

  const fetchPage = (cursor: string | undefined, signal: AbortSignal) => {
    return withAbortTimeout(
      (requestSignal) => workflowFetch<RunStepsResponse>(
        auth,
        workflowStepsRequestPath(runId, source, statusCsv, view, parentKey, cursor),
        "Failed to fetch workflow steps",
        { signal: requestSignal },
      ),
      30_000,
      signal,
      "Workflow steps request timed out",
    );
  };
  const waitForWorkflowStepRetry = (retryAfterSeconds: number | undefined, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason ?? new Error("Workflow steps request aborted"));
      return;
    }
    const timeout = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, workflowStepWarmingRefetchInterval(retryAfterSeconds));
    const onAbort = () => {
      clearTimeout(timeout);
      reject(signal.reason ?? new Error("Workflow steps request aborted"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });

  const headQuery = useQuery({
    queryKey: headQueryKey,
    enabled,
    queryFn: ({ signal }) => fetchPage(undefined, signal),
    retry: false,
    refetchInterval: (query) => workflowStepHeadRefetchInterval({
      active,
      status: query.state.data?.status,
      coverage: query.state.data?.coverage,
      retryAfterSeconds: Math.max(
        query.state.data?.retryAfterSeconds ?? 1,
        observabilityAdvanceGate.retryAfterSeconds(advanceKey) ?? 0,
      ),
    }),
  });
  const historyQuery = useInfiniteQuery(
    {
      ...workflowStepHistoryQueryOptions({
      queryKey: historyQueryKey,
      initialPageParam: workflowStepHistoryInitialPageParam(
        historyState.current.startCursor,
        headQuery.data?.cursor,
      ),
      queryFn: ({ pageParam, signal }) => {
        if (historyState.current.startCursor === undefined) {
          historyState.current.startCursor = pageParam;
        }
        if (!pageParam) throw new Error("Workflow step history cursor unavailable");
        return fetchWorkflowStepPageUntilReady(
          () => fetchPage(pageParam, signal),
          (retryAfterSeconds) => waitForWorkflowStepRetry(retryAfterSeconds, signal),
        );
      },
      }),
  });

  const warming = workflowStepsWarming(headQuery.data)
    || (historyQuery.data?.pages ?? []).some(workflowStepsWarming);
  const stepQueryKeys = useMemo(
    () => [headQueryKey, historyQueryKey] as const,
    [headQueryKey, historyQueryKey],
  );
  useWorkflowObservabilityAdvance({
    auth,
    runId,
    source,
    enabled,
    warming,
    observedAt: Math.max(headQuery.dataUpdatedAt, historyQuery.dataUpdatedAt),
    queryKeys: stepQueryKeys,
  });

  const mergedHead = useMemo(() => {
    if (!headQuery.data) return headArchive.current.page;
    const previous = headArchive.current.page;
    const historicalPagesLoaded = (historyQuery.data?.pages.length ?? 0) > 0;
    const merged = mergeWorkflowStepHeadSnapshot(headQuery.data, previous, historicalPagesLoaded);
    headArchive.current.page = merged;
    return merged;
  }, [headQuery.data, historyQuery.data?.pages.length, identity]);

  const data = useMemo(() => {
    if (!mergedHead) return undefined;
    return {
      pages: [mergedHead, ...(historyQuery.data?.pages ?? [])],
      pageParams: [undefined, ...(historyQuery.data?.pageParams ?? [])],
    };
  }, [historyQuery.data, mergedHead]);
  const hasHistory = (historyQuery.data?.pages.length ?? 0) > 0;
  const hasNextPage = hasHistory
    ? !!historyQuery.hasNextPage
    : !!mergedHead?.hasMore && !!mergedHead.cursor?.trim();

  return {
    data,
    isLoading: headQuery.isLoading,
    isError: headQuery.isError || historyQuery.isError,
    isFetching: headQuery.isFetching || historyQuery.isFetching,
    isFetchingNextPage: historyQuery.isFetchingNextPage,
    isFetchNextPageError: historyQuery.isFetchNextPageError,
    isHistoryError: historyQuery.isError,
    refetchHistory: historyQuery.refetch,
    isRefetchError: headQuery.isRefetchError,
    isWarming: warming,
    hasNextPage,
    fetchNextPage: historyQuery.fetchNextPage,
    refetch: headQuery.refetch,
  };
}

export function useWorkflowRunStepBranch(
  runId: string,
  active: boolean,
  source = DEFAULT_WORKFLOW_SOURCE,
  statuses: readonly string[] = [],
  parentKey: string,
  refreshActive = false,
) {
  const auth = useStoredAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const statusCsv = workflowStepStatusCsv(statuses);
  const enabled = workflowStepBranchRequestEnabled({
    hasSession: hasStoredSession(auth), runId, expanded: active,
  });
  const queryKey = useMemo(
    () => ["workflows", "run-step-branch", source, runId, statusCsv, parentKey] as const,
    [parentKey, runId, source, statusCsv],
  );
  const queryKeys = useMemo(() => [queryKey] as const, [queryKey]);
  const advanceKey = `${source}\u0000${runId}`;
  const query = useInfiniteQuery({
    queryKey,
    enabled: enabled && !!parentKey,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ source, order: "desc", limit: "100", view: "tree", parentKey });
      if (statusCsv) params.set("statuses", statusCsv);
      if (pageParam) params.set("cursor", pageParam);
      return withAbortTimeout(
        (innerSignal) => workflowFetch<RunStepsResponse>(
          authRef.current,
          `/runs/${encodeURIComponent(runId)}/steps?${params}`,
          "Failed to fetch workflow branch",
          { signal: innerSignal },
        ), 30_000, signal, "Workflow branch request timed out");
    },
    getNextPageParam: (lastPage, _allPages, _lastPageParam, allPageParams) =>
      workflowStepBranchNextCursor(lastPage, allPageParams),
    refetchInterval: (query) => {
      const warmingPage = query.state.data?.pages.find(workflowStepsWarming);
      const warming = !!warmingPage;
      if (warmingPage) {
        return workflowStepWarmingRefetchInterval(Math.max(
          warmingPage.retryAfterSeconds ?? 1,
          observabilityAdvanceGate.retryAfterSeconds(advanceKey) ?? 0,
        ));
      }
      return workflowStepBranchPollingEnabled({ active: refreshActive, warming })
        ? workflowStepRefetchInterval({ active: true })
        : false;
    },
    retry: false,
  });
  const warming = query.data?.pages.some(workflowStepsWarming) ?? false;
  useWorkflowObservabilityAdvance({
    auth,
    runId,
    source,
    enabled: enabled && !!parentKey,
    warming,
    observedAt: query.dataUpdatedAt,
    queryKeys,
  });
  return {
    ...query,
    retry: async () => {
      const result = await query.refetch();
      return !result.isError;
    },
  };
}

export function useWorkflowRunEvents(
  runId: string,
  active: boolean,
  source = DEFAULT_WORKFLOW_SOURCE,
) {
  const auth = useStoredAuth();
  return useInfiniteQuery({
    queryKey: ["workflows", "run-events", source, runId],
    enabled: hasStoredSession(auth) && !!runId,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ source, order: "desc", limit: "200" });
      if (pageParam) params.set("cursor", pageParam);
      return workflowFetch<RunEventsResponse>(
        auth,
        `/runs/${encodeURIComponent(runId)}/events?${params}`,
        "Failed to fetch workflow events",
        { signal },
      );
    },
    getNextPageParam: (last) => (last.hasMore ? last.cursor ?? undefined : undefined),
    refetchInterval: active ? 5_000 : false,
  });
}

export function useWorkflowRunLogs(
  runId: string,
  source = DEFAULT_WORKFLOW_SOURCE,
  limit = 200,
) {
  const auth = useStoredAuth();
  return useInfiniteQuery({
    queryKey: ["workflows", "run-logs", source, runId, limit],
    enabled: hasStoredSession(auth) && !!runId,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ source, limit: String(limit) });
      if (pageParam) params.set("cursor", pageParam);
      return workflowFetch<RunLogsResponse>(auth, `/runs/${encodeURIComponent(runId)}/logs?${params}`, "Failed to fetch workflow logs");
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
  });
}

export function useWorkflowRunLogsStream(
  runId: string,
  enabled: boolean,
  source = DEFAULT_WORKFLOW_SOURCE,
) {
  const auth = useStoredAuth();
  const [lines, setLines] = useState<LogLine[]>([]);
  const [logGroupConfigured, setLogGroupConfigured] = useState<boolean | null>(null);
  useEffect(() => {
    setLines([]);
    setLogGroupConfigured(null);
    if (!enabled || !hasStoredSession(auth) || !runId) return;
    const seen = new Set<string>();
    return subscribeAuthenticatedSse({
      baseUrl: WORKFLOWS_BASE,
      auth,
      path: `/runs/${encodeURIComponent(runId)}/logs/stream?source=${encodeURIComponent(source)}`,
      onMessage: ({ event, data }) => {
        try {
          if (event === "meta") {
            const meta = JSON.parse(data) as { logGroupConfigured?: boolean };
            if (typeof meta.logGroupConfigured === "boolean") setLogGroupConfigured(meta.logGroupConfigured);
          } else if (event === "log") {
            const line = JSON.parse(data) as LogLine;
            if (seen.has(line.eventId)) return;
            seen.add(line.eventId);
            setLines((current) => [...current, line]);
          }
        } catch { /* Ignore keep-alive or malformed frames. */ }
      },
    });
  }, [auth, enabled, runId, source]);
  return { lines, logGroupConfigured };
}

export function useRunArtefacts(
  runId: string,
  source = DEFAULT_WORKFLOW_SOURCE,
  stepId?: string,
) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["workflows", "artefacts", source, runId, stepId ?? null],
    enabled: hasStoredSession(auth) && !!runId,
    queryFn: () => {
      const params = new URLSearchParams({ source });
      if (stepId) params.set("stepId", stepId);
      return workflowFetch<ListArtefactsResponse>(auth, `/runs/${encodeURIComponent(runId)}/artifacts?${params}`, "Failed to fetch workflow artefacts");
    },
    staleTime: 30_000,
  });
}

export async function fetchWorkflowArtefact(
  auth: StoredAuth | null,
  runId: string,
  name: string,
  source = DEFAULT_WORKFLOW_SOURCE,
  stepId?: string,
): Promise<GetArtefactResponse> {
  const params = new URLSearchParams({ source });
  if (stepId) params.set("stepId", stepId);
  return workflowFetch<GetArtefactResponse>(auth, `/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(name)}?${params}`, "Failed to fetch workflow artefact");
}

export async function fetchWorkflowArtefactContent(
  auth: StoredAuth | null,
  runId: string,
  name: string,
  source = DEFAULT_WORKFLOW_SOURCE,
  stepId?: string,
): Promise<string> {
  const params = new URLSearchParams({ source });
  if (stepId) params.set("stepId", stepId);
  const response = await authedFetch(
    WORKFLOWS_BASE,
    auth,
    `/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(name)}/content?${params}`,
  );
  if (!response.ok) throw new Error(`Failed to fetch workflow artefact content: ${response.status}`);
  return response.text();
}

export function useWorkflowEmailSearch(term: string) {
  const auth = useStoredAuth();
  const query = term.trim();
  return useQuery({
    queryKey: ["workflows", "email-search", query],
    enabled: hasStoredSession(auth) && !!query,
    queryFn: () =>
      workflowFetch<EmailSearchResponse>(auth, `/email-search?${new URLSearchParams({ q: query })}`, "Failed to search workflow emails"),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useEnqueueWorkflow(source = DEFAULT_WORKFLOW_SOURCE) {
  const auth = useStoredAuth();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { workflowName: string; idempotencyKey: string; input?: unknown }) =>
      workflowFetch<{ runId: string; deduplicated: boolean }>(auth, "/runs", "Failed to enqueue workflow", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["workflows", "active", source] });
      void client.invalidateQueries({ queryKey: ["workflows", "runs", source] });
    },
  });
}

function useWorkflowRunMutation(kind: "cancel" | "retry" | "pause" | "resume", source = DEFAULT_WORKFLOW_SOURCE) {
  const auth = useStoredAuth();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      workflowFetch<{
        runId: string;
        status?: string;
        deduplicated?: boolean;
        attempt?: number;
        pauseSupported?: boolean;
        pauseRequestedAt?: number | null;
        pauseAcknowledgedAt?: number | null;
        controlState?: "running" | "pause_requested" | "paused";
      }>(
        auth,
        `/runs/${encodeURIComponent(runId)}/${kind}`,
        `Failed to ${kind} workflow run`,
        { method: "POST" },
      ),
    onSuccess: (data, runId) => {
      if (kind === "pause" || kind === "resume") {
        client.setQueryData<RunDetailResponse>(["workflows", "run-detail", source, runId], (previous) =>
          previous ? {
            ...previous,
            run: {
              ...previous.run,
              pauseSupported: data.pauseSupported,
              pauseRequestedAt: data.pauseRequestedAt,
              pauseAcknowledgedAt: data.pauseAcknowledgedAt,
              controlState: data.controlState,
            },
          } : previous,
        );
      }
      void client.invalidateQueries({ queryKey: ["workflows", "run-detail", source, runId] });
      void client.invalidateQueries({ queryKey: ["workflows", "active", source] });
      void client.invalidateQueries({ queryKey: ["workflows", "runs", source] });
    },
  });
}

export const useCancelWorkflowRun = (source = DEFAULT_WORKFLOW_SOURCE) =>
  useWorkflowRunMutation("cancel", source);
export const useRetryWorkflowRun = (source = DEFAULT_WORKFLOW_SOURCE) =>
  useWorkflowRunMutation("retry", source);
export const usePauseWorkflowRun = (source = DEFAULT_WORKFLOW_SOURCE) =>
  useWorkflowRunMutation("pause", source);
export const useResumeWorkflowRun = (source = DEFAULT_WORKFLOW_SOURCE) =>
  useWorkflowRunMutation("resume", source);

export type FailedStepRetryPlan = {
  runId: string;
  eligible: boolean;
  code: string;
  reason: string;
  failedStepCount: number;
  completedStepCount: number;
  pendingStepCount: number;
  existingRetryRunId?: string;
};

export function useFailedStepRetryPlan(runId: string, enabled: boolean, source = DEFAULT_WORKFLOW_SOURCE) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["workflows", "failed-step-retry", source, runId],
    enabled: enabled && hasStoredSession(auth) && !!runId,
    queryFn: () => workflowFetch<FailedStepRetryPlan>(
      auth,
      `/runs/${encodeURIComponent(runId)}/retry-failed`,
      "Could not check failed-step retry",
    ),
    staleTime: 30_000,
    retry: false,
  });
}

export function useRetryFailedWorkflowSteps(source = DEFAULT_WORKFLOW_SOURCE) {
  const auth = useStoredAuth();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => workflowFetch<{
      runId: string;
      deduplicated: boolean;
      attempt: number;
      mode: "failed";
      sourceRunId: string;
    }>(auth, `/runs/${encodeURIComponent(runId)}/retry-failed`, "Could not retry failed steps", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }),
    onSettled: (_data, _error, runId) => {
      void client.invalidateQueries({ queryKey: ["workflows", "failed-step-retry", source, runId] });
      void client.invalidateQueries({ queryKey: ["workflows", "run-detail", source, runId] });
      void client.invalidateQueries({ queryKey: ["workflows", "active", source] });
      void client.invalidateQueries({ queryKey: ["workflows", "runs", source] });
    },
  });
}
